import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { deleteDoc, doc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';

import { useToast } from '~/hooks/toast';

import { useMenus } from '~/hooks/menus';
import { useAuth } from '~/hooks/auth';
import IMenu from '~/interfaces/IMenu';
import { bff } from '~/services/bff';

const { REACT_APP_FIREBASE_PROJECT } = process.env;

const Menus: React.FC = () => {
  const { addToast } = useToast();
  const { menus, loadMenus, removeMenu, isInitialized } = useMenus();
  const { company } = useAuth();
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    if (!isInitialized && company.uid) loadMenus(company.uid);
  }, [loadMenus, isInitialized, company]);

  const handleConfirmRemove = useCallback(
    async (item: IMenu) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirma a exclusão do cardápio?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        await deleteDoc(doc(DB, 'menus', item.uid));
        await bff.delete(`/menus/${REACT_APP_FIREBASE_PROJECT}`);
        removeMenu(item.uid);

        addToast({
          type: 'success',
          description: 'Cardápio foi removido com sucesso.',
        });
      }
    },
    [addToast, removeMenu],
  );

  return (
    <Dashboard containerMessage="col-xl-8 offset-xl-2">
      <div className="col-xl-8 offset-xl-2">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h3 className="nk-block-title page-title">Cardápios</h3>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li className="nk-block-tools-opt">
                      <Link
                        to="/menus/create"
                        data-target="addProduct"
                        className="toggle btn btn-icon btn-primary d-md-none"
                      >
                        <em className="icon ni ni-plus" />
                      </Link>
                      <Link
                        to="/menus/create"
                        data-target="addProduct"
                        className="toggle btn btn-primary d-none d-md-inline-flex"
                      >
                        <em className="icon ni ni-plus" />
                        <span>Criar Cardápio</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="nk-block">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <div className="nk-tb-list">
                  <div className="nk-tb-item nk-tb-head">
                    <div className="nk-tb-col">
                      <span>Nome</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Horário de Início</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Horário de Término</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Status</span>
                    </div>

                    <div className="nk-tb-col nk-tb-col-tools">
                      <ul className="nk-tb-actions gx-1 my-n1">
                        <li className="mr-n1" />
                      </ul>
                    </div>
                  </div>

                  {menus.length === 0 && (
                    <div className="nk-tb-item">
                      <div className="nk-tb-col">
                        <span>Você ainda não possui cardápios.</span>
                      </div>
                    </div>
                  )}

                  {menus.map(menu => (
                    <div className="nk-tb-item" key={menu.uid}>
                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span>{menu.name}</span>
                        </div>
                      </div>
                      <div className="nk-tb-col">
                        <span>{menu.initHour}</span>
                      </div>
                      <div className="nk-tb-col">
                        <span>{menu.endHour}</span>
                      </div>

                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span
                            className={`text-${
                              menu.status ? 'success' : 'danger'
                            } fs-12px`}
                          >
                            {menu.status && <span>Ativo</span>}
                            {!menu.status && <span>Inativo</span>}
                          </span>
                        </div>
                      </div>

                      <div className="nk-tb-col nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1 my-n1">
                          <li className="mr-n1">
                            <div className="dropdown">
                              <Link
                                to="#options"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                                data-toggle="dropdown"
                              >
                                <em className="icon ni ni-more-h" />
                              </Link>
                              <div className="dropdown-menu dropdown-menu-right">
                                <ul className="link-list-opt no-bdr">
                                  <li>
                                    <Link
                                      to={{
                                        pathname: `/menus/edit/${menu.uid}`,
                                        state: menu,
                                      }}
                                    >
                                      <em className="icon ni ni-edit" />
                                      <span>Editar</span>
                                    </Link>
                                  </li>

                                  <li>
                                    <Link
                                      to="#remove"
                                      onClick={() => {
                                        handleConfirmRemove(menu);
                                      }}
                                    >
                                      <em className="icon ni ni-trash" />
                                      <span>Excluir</span>
                                    </Link>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default Menus;
